import { useEffect, useState } from "react";

import { trackCliSetupCopy } from "@/lib/analytics";
import { API_BASE, getDefaultApiKey, setStoredApiKey, subscribeApiKey } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// "Use SkillPatch from Agents" card — the one-command CLI setup. Self-contained (fetches the
// caller's default API key). Shared by Settings and the Home feed. Renders nothing when logged out.
export default function UseFromAgents({ className = "" }: { className?: string }) {
  const { accessToken } = useAuth();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [os, setOs] = useState<"unix" | "windows">(() =>
    typeof navigator !== "undefined" && /win/i.test(navigator.userAgent) ? "windows" : "unix",
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    getDefaultApiKey(accessToken)
      .then((k) => { if (k) { setApiKey(k.key); setStoredApiKey(k.key); } })
      .catch(() => {});
  }, [accessToken]);

  // Refresh the setup command in place when the key is rotated elsewhere (Settings → Regenerate),
  // so the copied command never contains a now-revoked key.
  useEffect(() => subscribeApiKey((k) => setApiKey(k)), []);

  if (!accessToken) return null;

  const base = API_BASE || (typeof window !== "undefined" ? window.location.origin : "");
  const wrap = (key: string) => (os === "windows" ? `irm ${base}/setup/${key} | iex` : `curl -sSL ${base}/setup/${key} | bash`);
  // Real command (copied) vs. the on-screen version — everything after a short recognizable prefix
  // is masked so a screenshot can't leak the key, while Copy still writes the full real command.
  const maskKey = (k: string) => (k.length > 8 ? `${k.slice(0, 8)}***` : "***");
  const setupCmd = apiKey ? wrap(apiKey) : "";
  const displayCmd = apiKey ? wrap(maskKey(apiKey)) : "";

  async function copy() {
    if (!setupCmd) return;
    await navigator.clipboard.writeText(setupCmd);
    trackCliSetupCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <section className={`rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.14] to-primary/[0.04] p-6 ${className}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-white">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" /></svg>
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-semibold">Add SkillPatch to Your Agent</h2>
        </div>
      </div>

      <div className="mt-5 inline-flex rounded-lg border p-0.5 text-xs font-medium" style={{ borderColor: "var(--border)", background: "var(--glass-strong)" }}>
        {([["unix", "macOS / Linux"], ["windows", "Windows"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setOs(key)}
            className={`rounded-md px-3 py-1.5 transition ${os === key ? "bg-primary text-white" : "text-text/60 hover:text-text"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-3 rounded-xl border px-4 py-3" style={{ background: "var(--bg-subtle)", borderColor: "var(--border)" }}>
        <code className="flex-1 truncate font-mono text-sm text-text/80">{displayCmd || "Loading…"}</code>
        <button
          onClick={copy}
          disabled={!setupCmd}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
        >
          <CopyIcon />
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      {/* <p className="mt-2 text-xs text-text/40">Your API key is hidden here — <span className="text-text/60">Copy</span> pastes the real command.</p> */}

      {os === "windows" && (
        <p className="mt-3 text-xs text-text/40">
          Run this in <span className="text-text/70">PowerShell</span> and relaunch the terminal / app.
        </p>
      )}
      <p className="mt-2 text-xs text-text/40">
        Then open your agent and type <code className="rounded px-1 py-0.5" style={{ background: "var(--glass-strong)" }}>/skillpatch browse</code> to get started.
      </p>
    </section>
  );
}
